import React, { useEffect, useState } from 'react';

/**
 * NetworkBackdrop — shared neural-mesh backdrop for the entire app.
 *
 * Renders a fixed-position SVG `<pattern>` of nodes + connecting lines
 * tiled to fill any viewport (mobile to ultrawide). Sits at z-index 0
 * so all UI content (cards, panels, modals) layers above naturally.
 *
 * Props:
 *   - opacity:  number — 0..1, default 0.05 (auth app subtlety)
 *   - color:    string — stroke/fill color, default #1a2b5b (brand navy)
 *   - animate:  boolean — pulse animation on a subset of nodes, default true
 *
 * Respects `prefers-reduced-motion: reduce` automatically.
 */
function NetworkBackdrop({ opacity = 0.05, color = '#1a2b5b', animate = true }) {
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(mq.matches);
    const listener = (e) => setReduceMotion(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', listener);
    else mq.addListener(listener);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', listener);
      else mq.removeListener(listener);
    };
  }, []);

  const shouldAnimate = animate && !reduceMotion;
  const patternId = React.useId();

  // Stable pulse offsets for the pulse nodes — lets each node breathe with a slight stagger.
  const pulses = [
    { cx: 120, cy: 80,  delay: '0s' },
    { cx: 300, cy: 100, delay: '1.1s' },
    { cx: 160, cy: 180, delay: '0.5s' },
    { cx: 100, cy: 280, delay: '2.2s' },
    { cx: 20,  cy: 320, delay: '1.7s' },
  ];

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 0,
        overflow: 'hidden',
      }}
    >
      <style>{`
        .net-bd-line { stroke: ${color}; stroke-width: 0.6; fill: none; }
        .net-bd-node { fill: ${color}; }
        ${shouldAnimate ? `
          @keyframes netBdPulse {
            0%, 100% { opacity: 0.35; }
            50%      { opacity: 1; }
          }
          .net-bd-pulse {
            animation: netBdPulse 4s ease-in-out infinite;
            transform-origin: center;
            transform-box: fill-box;
          }
        ` : ''}
      `}</style>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid slice"
        style={{ opacity }}
      >
        <defs>
          <pattern
            id={patternId}
            x="0"
            y="0"
            width="320"
            height="320"
            patternUnits="userSpaceOnUse"
          >
            <line x1="20"  y1="40"  x2="120" y2="80"  className="net-bd-line" />
            <line x1="120" y1="80"  x2="220" y2="40"  className="net-bd-line" />
            <line x1="220" y1="40"  x2="300" y2="100" className="net-bd-line" />
            <line x1="20"  y1="40"  x2="60"  y2="160" className="net-bd-line" />
            <line x1="60"  y1="160" x2="160" y2="180" className="net-bd-line" />
            <line x1="160" y1="180" x2="260" y2="140" className="net-bd-line" />
            <line x1="260" y1="140" x2="300" y2="100" className="net-bd-line" />
            <line x1="60"  y1="160" x2="100" y2="280" className="net-bd-line" />
            <line x1="100" y1="280" x2="200" y2="260" className="net-bd-line" />
            <line x1="200" y1="260" x2="260" y2="140" className="net-bd-line" />
            <line x1="200" y1="260" x2="300" y2="300" className="net-bd-line" />
            <line x1="100" y1="280" x2="20"  y2="320" className="net-bd-line" />
            <circle cx="20"  cy="40"  r="1.5" className="net-bd-node" />
            <circle cx="220" cy="40"  r="1.5" className="net-bd-node" />
            <circle cx="60"  cy="160" r="1.5" className="net-bd-node" />
            <circle cx="260" cy="140" r="1.5" className="net-bd-node" />
            <circle cx="200" cy="260" r="1.5" className="net-bd-node" />
            <circle cx="300" cy="300" r="1.5" className="net-bd-node" />
            <circle cx="320" cy="40"  r="1.5" className="net-bd-node" />
            <circle cx="320" cy="320" r="1.5" className="net-bd-node" />
            <circle cx="0"   cy="40"  r="1.5" className="net-bd-node" />
            {pulses.map((p, i) => (
              <circle
                key={i}
                cx={p.cx}
                cy={p.cy}
                r="2.5"
                className={`net-bd-node ${shouldAnimate ? 'net-bd-pulse' : ''}`}
                style={shouldAnimate ? { animationDelay: p.delay } : undefined}
              />
            ))}
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${patternId})`} />
      </svg>
    </div>
  );
}

export default NetworkBackdrop;
